"use client"

import type { components } from "umbraco"
import { replaceRichTextLinks, sanitizeAnchorId } from "utils"
import Container from "../../Container"
import { Root, SectionTitle, RichTextWrapper } from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type RichContentData =
  components["schemas"]["RichContentBlockPropertiesModel"]

interface Props {
  data: RichContentData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
// `sanitizeAnchorId` (from utils) is shared with PageBlocks so the rendered
// block id always matches the nav href the rail builds.
// EGDE-76: each Rich Content Block carries ONE anchor (its `anchorId`). The
// block sets that id on its Root so the page-level RichContentAnchorNav can
// scroll to it; the nav uses the block's `sectionTitle` as the visible label.
// (The earlier multi-anchor heading-injection approach was rejected by QA — the
// intended model is one anchor per block, multiple blocks for multiple anchors.)
export default function RichContent({ data }: Props) {
  const rawMarkup = data.content?.markup ?? ""
  const finalMarkup = rawMarkup ? replaceRichTextLinks(rawMarkup) : ""
  const anchorId = sanitizeAnchorId(data.anchorId)

  if (!finalMarkup && !data.sectionTitle) return null

  return (
    <Container paddingMobile>
      <Root
        data-block="rich-content-block"
        {...(anchorId ? { id: anchorId } : {})}
      >
        {data.sectionTitle && (
          <SectionTitle variant="h2">{data.sectionTitle}</SectionTitle>
        )}
        {finalMarkup && (
          <RichTextWrapper
            dangerouslySetInnerHTML={{ __html: finalMarkup }}
          />
        )}
      </Root>
    </Container>
  )
}
